import { CommonModule } from '@angular/common';
import { Component, inject, input, output } from '@angular/core';

// Services
import { ViewportService } from '../../../core/_services/viewport/viewport.service';

// Models
import { ButtonShape, ButtonVariant } from '../../_model/button.model';
import { NavigationItem } from '../../_model/navigation-item.model';
import { Theme } from '../../../core/_models/view-state.model';

// Directives
import { StateHandlerDirective } from '../../../core/_components/state-handler/state-handler.component';

// Components
import { ButtonComponent } from '../button/button.component';

@Component({
  selector: 'pp-navbar',
  templateUrl: './navbar.component.html',
  imports: [CommonModule, ButtonComponent],
})
export class NavbarComponent extends StateHandlerDirective {

  readonly ariaLabel = input<string>();
  readonly dynamic = input(false);
  readonly id = input.required<string>();
  readonly navigationItems = input.required<NavigationItem[]>();

  readonly itemSelected = output<string>();

  // Aliases
  buttonShape = ButtonShape;
  buttonVariant = ButtonVariant;

  // Services
  readonly viewportService = inject(ViewportService);

  /**
   * Checks to see if any navigational items are currently set to active.
   * @type {boolean}
   * @memberof ProfilePageNavbarComponent
   */
  get isNavbarItemActive(): boolean {
    return this.navigationItems().some(ni => ni.active);
  }

  /**
   * `true` if the viewport is at least 992px, or `false` otherwise.
   * This getter is required as using the viewport service does not display correct behaviour.
   * @type {boolean}
   * @memberof ProfilePageNavbarComponent
   */
  get isViewportLg(): boolean {
    return window.innerWidth >= 992;
  }

  /**
   * Called when the item has been clicked.
   * @param e the click event.
   * @param id the identifier of the item that was clicked on.
   * @memberof ProfilePageNavbarComponent
   */
  onItemClick(id: string): void {
    this.itemSelected.emit(id);
  }

  /**
   * Called when the key is pressed on an item.
   * @param e the keyboard event.
   * @param id the identifier of the item that was pressed on.
   * @memberof ProfilePageNavbarComponent
   */
  onItemKeyPress(e: KeyboardEvent, id: string) {
    if (e.key === 'Enter') this.itemSelected.emit(id);
  }

  /**
   * The resolved icon theme modifier setting to display the correct icon for the selected theme.
   * @param active the selected status of the item.
   * @returns the resolved icon colour to retrieve.
   * @memberof ProfilePageNavbarComponent
   */
  resolveIconThemeModifier(active: boolean): string {
    return this.selectedTheme === Theme.Dark && !active ? 'white' : 'black';
  }
}
